/**
 * 发票申请表单的纯逻辑（不依赖 React，可单测）
 * -----------------------------------------------------------------------------
 * 与 `components/FormRenderer/logic.ts` 同取向：**判定与渲染分开**。
 * 这里只做两件事：
 *   ① 把「表单草稿」收窄成契约要求的请求体（空值一律**省略**，不发空串）；
 *   ② 给出「必填项是否已填」的本地提示，避免提交一个必然 400 的表单。
 *
 * ## 边界（刻意不做的事）
 * - **不校验金额** —— 金额根本不在请求体里（服务端按缴费对账基准写入）。
 *   前端重算一遍减法只会多一处会漂移的口径。
 * - **不预设业务错误码文案** —— 「单位抬头必须有税号」对应服务端 `INVOICE-001`，
 *   但这里只是提醒「还没填」；**权威判定仍在服务端**，本地提示不冒充结论。
 * - **不做长度校验** —— 长度上限属服务端 `@MaxLength` 的形状约束，
 *   把数字抄到前端只会多一处要同步的常量；超长由服务端 400 兜住。
 */
import { InvoiceTitleType } from '@enroll/shared';
import type { InvoiceApplyInput } from '../../api/h5';

/** 抬头类型选项（文案直接在界面用；取值取自共享枚举，不写字符串字面量） */
export const INVOICE_TITLE_TYPE_OPTIONS: readonly {
  value: InvoiceTitleType;
  label: string;
}[] = [
  { value: InvoiceTitleType.PERSONAL, label: '个人' },
  { value: InvoiceTitleType.COMPANY, label: '单位' },
];

export interface InvoiceDraft {
  titleType: InvoiceTitleType;
  title: string;
  taxNo: string;
  remark: string;
}

/** 初始草稿。个人抬头默认带出报名人姓名（多数情况下本人就是抬头） */
export function initialInvoiceDraft(applicantName?: string | null): InvoiceDraft {
  return {
    titleType: InvoiceTitleType.PERSONAL,
    title: applicantName ?? '',
    taxNo: '',
    remark: '',
  };
}

/**
 * 表单还没填全时返回一句提示；已填全返回 `null`。
 *
 * 只判**契约里声明的必填项**（`required: [titleType, title]` +
 * `taxNo` 在单位抬头下必填），不发明额外规则。
 */
export function invoiceDraftProblem(draft: InvoiceDraft): string | null {
  if (draft.title.trim() === '') return '请填写发票抬头';
  if (draft.titleType === InvoiceTitleType.COMPANY && draft.taxNo.trim() === '') {
    return '单位抬头需填写纳税人识别号';
  }
  return null;
}

/**
 * 草稿 → 请求体。
 *
 * 空串**一律省略**而不是发 `''`：契约里 `taxNo` / `remark` 是可选字段，
 * 发空串等于把「没填」硬说成「填了空」，服务端还得再判一次空。
 */
export function invoicePayloadOf(draft: InvoiceDraft): InvoiceApplyInput {
  const taxNo = draft.taxNo.trim();
  const remark = draft.remark.trim();
  return {
    titleType: draft.titleType,
    title: draft.title.trim(),
    ...(draft.titleType === InvoiceTitleType.COMPANY && taxNo !== '' ? { taxNo } : {}),
    ...(remark !== '' ? { remark } : {}),
  };
}
